import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { storeService } from '../services/storeService';
import { StoreSeller, StoreProfile, PaymentMethod, InsufficientStockItem } from '../models';
import { useCartStore } from '../store/cartStore';
import { useAuthStore } from '../store/authStore';
import { useToastStore } from '../store/toastStore';
import { EmptyState } from '../components/ui/EmptyState';
import { Button } from '../components/ui/Button';
import { Link, useNavigate } from 'react-router-dom';
import { buildWhatsAppUrl } from '../utils/whatsapp';
import { getCheckoutTotals } from '../utils/pricing';
import { usePaymentSettings } from '../hooks/usePaymentSettings';
import { StepIndicator } from './checkout/StepIndicator';
import { GuestInfoStep } from './checkout/GuestInfoStep';
import { PaymentStep } from './checkout/PaymentStep';
import { ConfirmStep } from './checkout/ConfirmStep';
import { StockConflictNotice } from './checkout/StockConflictNotice';
import { SessionExpiredNotice } from './checkout/SessionExpiredNotice';
import { MobileOrderSummary, OrderSummaryCard } from './checkout/OrderSummary';
import { ChevronLeftIcon } from './checkout/CheckoutIcons';
import { validateGuestInfo } from './checkout/guestInfoValidation';
import {
  clearPersistedCheckoutState,
  generateId,
  loadPersistedCheckoutState,
  saveOrderConfirmation,
  savePersistedCheckoutState,
} from './checkout/checkoutStorage';
import {
  createPaymentEntry,
  createSingleMethodEntry,
  restorePaymentEntries,
  sumEntries,
  syncSingleEntry,
} from './checkout/paymentEntries';
import { CHECKOUT_MAX_WIDTH_CLASS, CHECKOUT_SIDEBAR_GRID_CLASS, TOUCH_TARGET_CLASS } from './checkout/styles';
import { ALL_PAYMENT_METHODS, GuestInfo, PaymentEntry, PaymentMode, Step } from './checkout/types';
import { extractInsufficientStock } from '../utils/reservation';
import { getCampaignCatalogHref } from '../config/promotionCampaign';
import { useInitEffect } from '../hooks/useInitEffect';
import { useCartContactSync } from '../hooks/useCartContactSync';
import { StoreEventType, trackStoreEvent } from '../services/trackingService';
import {
  setMetaPixelAdvancedMatching,
  trackAddPaymentInfo,
  trackCheckoutStep,
  trackInitiateCheckout,
  trackPurchase,
} from '../utils/metaPixel';

const PURCHASE_TRACKED_STORAGE_PREFIX = 'babychic-purchase-tracked-';
const ORDER_STATUS_AWAITING_PAYMENT = 6;
const ORDER_CHANNEL_STORE = 5;
const AMOUNT_TOLERANCE = 0.01;
const DEFAULT_ORDER_ERROR = 'Erro ao registrar pedido. Tente novamente.';

const DEFAULT_GUEST_INFO: GuestInfo = {
  name: '',
  phone: '',
  street: '',
  number: '',
  neighborhood: '',
  city: '',
  state: '',
  zipCode: '',
  complement: '',
  email: '',
  cpfCnpj: '',
  babies: [],
  lookupConfirmed: false,
};

interface ApiErrorBody {
  errors?: string[];
  message?: string;
}

function getOrderErrorMessage(err: unknown): string {
  if (!axios.isAxiosError(err)) return DEFAULT_ORDER_ERROR;
  const data = err.response?.data as ApiErrorBody | undefined;
  return data?.errors?.[0] || data?.message || DEFAULT_ORDER_ERROR;
}

function isUnauthorizedError(err: unknown): boolean {
  return axios.isAxiosError(err) && err.response?.status === 401;
}

export function CheckoutPage() {
  const { items, clearCart, applyAvailableStock } = useCartStore();
  const hasPromotionalItems = items.some((i) => i.promotionalPrice != null);
  const { isAuthenticated, customerId } = useAuthStore();
  const navigate = useNavigate();
  const showToast = useToastStore((s) => s.show);
  const { settings: paymentSettings } = usePaymentSettings();

  const [persisted] = useState(loadPersistedCheckoutState);

  const [sellers, setSellers] = useState<StoreSeller[]>([]);
  const [loadingSellers, setLoadingSellers] = useState(true);
  const [step, setStep] = useState<Step>(() => {
    const initial = persisted.step ?? (isAuthenticated ? 'payment' : 'guest-info');
    return isAuthenticated && initial === 'guest-info' ? 'payment' : initial;
  });
  const [selectedSeller, setSelectedSeller] = useState<StoreSeller | null>(null);
  const [customerProfile, setCustomerProfile] = useState<StoreProfile | null>(null);
  const [guestInfo, setGuestInfo] = useState<GuestInfo>(() => persisted.guestInfo ?? DEFAULT_GUEST_INFO);
  const [paymentEntries, setPaymentEntries] = useState<PaymentEntry[]>(
    () =>
      restorePaymentEntries(persisted, items, paymentSettings) ?? [
        createSingleMethodEntry(PaymentMethod.Pix, items, paymentSettings),
      ]
  );
  const [paymentMode, setPaymentMode] = useState<PaymentMode>(persisted.paymentMode ?? 'combine');
  const [observation, setObservation] = useState(persisted.observation ?? '');
  const [idempotencyKey] = useState(() => persisted.idempotencyKey ?? generateId());
  const [submitting, setSubmitting] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [stockConflict, setStockConflict] = useState<{ items: InsufficientStockItem[]; applied: boolean } | null>(null);

  const isFirstRender = useRef(true);
  const wasAuthenticatedRef = useRef(isAuthenticated);
  const trackedStepRef = useRef<Step | null>(null);
  const submittingRef = useRef(false);
  const completedRef = useRef(false);

  useCartContactSync(guestInfo.name, guestInfo.phone, !isAuthenticated);

  const totals = getCheckoutTotals(items, paymentEntries, paymentSettings);
  const totalPaid = sumEntries(paymentEntries);
  const paymentRemainder = totals.net - totalPaid;
  const installmentsInterestFree = paymentSettings?.installmentsWithoutInterest ?? false;

  useInitEffect(() => {
    if (items.length === 0) return;
    trackInitiateCheckout(items);
  });

  useEffect(() => {
    if (items.length === 0 || trackedStepRef.current === step) return;
    const previousStep = trackedStepRef.current;
    trackedStepRef.current = step;
    trackCheckoutStep(step, items);
    trackStoreEvent({ type: StoreEventType.CheckoutStep, metadata: { step } });
    if (previousStep === 'payment' && step === 'confirm') {
      trackAddPaymentInfo(items, paymentEntries);
    }
  }, [step, items, paymentEntries]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  useEffect(() => {
    if (wasAuthenticatedRef.current && !isAuthenticated) {
      setStep('guest-info');
      setCustomerProfile(null);
    }
    if (isAuthenticated) setSessionExpired(false);
    wasAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      if (step === 'guest-info') setStep('payment');
      return;
    }
    if (step !== 'guest-info' && Object.keys(validateGuestInfo(guestInfo)).length > 0) {
      setStep('guest-info');
    }
  }, [isAuthenticated, step, guestInfo]);

  useEffect(() => {
    if (step === 'confirm' && !selectedSeller && !loadingSellers) {
      setStep('payment');
    }
  }, [step, selectedSeller, loadingSellers]);

  useEffect(() => {
    storeService
      .getSellers()
      .then((data) => {
        setSellers(data);
        const found = persisted.selectedSellerId
          ? data.find((s) => s.userId === persisted.selectedSellerId)
          : undefined;
        if (found) {
          setSelectedSeller(found);
        } else if (data.length === 1) {
          setSelectedSeller(data[0]);
        }
      })
      .finally(() => setLoadingSellers(false));
    if (customerId) {
      storeService.getProfile().then(setCustomerProfile).catch(() => {});
    }
  }, [customerId, persisted]);

  useEffect(() => {
    if (!hasPromotionalItems) return;
    setPaymentEntries((prev) =>
      prev.some((e) => e.installments != null && e.installments !== 1)
        ? prev.map((e) => (e.installments != null ? { ...e, installments: 1 } : e))
        : prev
    );
  }, [hasPromotionalItems]);

  useEffect(() => {
    if (paymentMode !== 'combine' || paymentEntries.length !== 1) return;
    if (Math.abs(paymentEntries[0].amountPaid - totals.net) < AMOUNT_TOLERANCE) return;
    setPaymentEntries([{ ...paymentEntries[0], amountPaid: totals.net }]);
  }, [paymentMode, paymentEntries, totals.net]);

  useEffect(() => {
    if (completedRef.current) return;
    savePersistedCheckoutState({
      step,
      guestInfo,
      paymentEntries,
      paymentMode,
      observation,
      selectedSellerId: selectedSeller?.userId,
      idempotencyKey,
      cartTotal: totals.gross,
    });
  }, [step, guestInfo, paymentEntries, paymentMode, observation, selectedSeller, idempotencyKey, totals.gross]);

  if (isRedirecting) return null;

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        {stockConflict && (
          <StockConflictNotice
            items={stockConflict.items}
            applied={stockConflict.applied}
            onApply={() => undefined}
            onDismiss={() => setStockConflict(null)}
          />
        )}
        <EmptyState
          title={stockConflict ? 'Sua sacola ficou vazia' : 'Sacola vazia'}
          description={stockConflict ? 'As peças que você escolheu não estão mais disponíveis.' : undefined}
          action={<Link to={getCampaignCatalogHref()}><Button>Ver produtos</Button></Link>}
        />
      </div>
    );
  }

  const addPaymentMethod = () => {
    const used = new Set(paymentEntries.map((e) => e.paymentMethod));
    const next = ALL_PAYMENT_METHODS.find((pm) => !used.has(pm));
    if (!next) return;
    setPaymentEntries((prev) => [...prev, createPaymentEntry(next, 0)]);
  };

  const removePaymentMethod = (index: number) => {
    setPaymentEntries((prev) =>
      syncSingleEntry(prev, prev.filter((_, i) => i !== index), items, paymentSettings)
    );
  };

  const updatePaymentEntry = (index: number, field: keyof PaymentEntry, value: number | PaymentMethod) => {
    setPaymentEntries((prev) => {
      const next = prev.map((e, i) => {
        if (i !== index) return e;
        const updated = { ...e, [field]: value };
        if (field === 'paymentMethod') {
          updated.installments = (value as PaymentMethod) === PaymentMethod.CreditCard ? 1 : undefined;
        }
        if (field === 'installments' && hasPromotionalItems) {
          updated.installments = 1;
        }
        return updated;
      });
      return field === 'paymentMethod' ? syncSingleEntry(prev, next, items, paymentSettings) : next;
    });
  };

  const fillRemainder = (index: number) => {
    const othersPaid = paymentEntries
      .filter((_, i) => i !== index)
      .reduce((sum, e) => sum + e.amountPaid, 0);
    updatePaymentEntry(index, 'amountPaid', Math.max(0, totals.net - othersPaid));
  };

  const isPaymentValid = () => {
    if (paymentEntries.some((e) => e.amountPaid <= 0)) return false;
    return Math.abs(paymentRemainder) < AMOUNT_TOLERANCE;
  };

  const selectCombinePaymentMethod = (method: PaymentMethod) => {
    setPaymentEntries([createSingleMethodEntry(method, items, paymentSettings)]);
  };

  const switchToCombinePayment = () => {
    setPaymentMode('combine');
    setPaymentEntries([createSingleMethodEntry(PaymentMethod.Pix, items, paymentSettings)]);
  };

  const applyStockConflict = () => {
    if (!stockConflict) return;
    stockConflict.items.forEach((item) => applyAvailableStock(item.productVariantId, item.available));
    const updatedItems = useCartStore.getState().items;
    setPaymentMode('combine');
    setPaymentEntries([createSingleMethodEntry(PaymentMethod.Pix, updatedItems, paymentSettings)]);
    setStockConflict({ ...stockConflict, applied: true });
  };

  const trackPurchaseOnce = (orderId: number, email?: string, phone?: string) => {
    try {
      const trackedKey = `${PURCHASE_TRACKED_STORAGE_PREFIX}${orderId}`;
      if (sessionStorage.getItem(trackedKey)) return;
      setMetaPixelAdvancedMatching({ email, phone });
      trackPurchase(orderId, items, totals.net);
      sessionStorage.setItem(trackedKey, '1');
    } catch {
    }
  };

  const handleSubmit = async () => {
    if (!selectedSeller || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setStockConflict(null);
    setSessionExpired(false);

    try {
      const submittedEntries = paymentEntries.map((e) => ({
        ...e,
        amountPaid: totals.pixDiscountApplies && paymentEntries.length === 1 ? totals.net : e.amountPaid,
      }));

      const orderItems = items.map((i) => {
        const unitPrice = i.promotionalPrice ?? i.price;
        return {
          productId: i.productId,
          productVariantId: i.variantId,
          quantity: i.quantity,
          unitPrice,
          totalPrice: unitPrice * i.quantity,
          isGift: i.isGift ?? false,
        };
      });

      const isGuest = !isAuthenticated;
      const guestName = guestInfo.name.trim();

      const orderId = await storeService.createOrder({
        customerId: isGuest ? undefined : customerId!,
        sellerId: selectedSeller.userId,
        status: ORDER_STATUS_AWAITING_PAYMENT,
        channel: ORDER_CHANNEL_STORE,
        saleDate: new Date().toISOString(),
        observation: observation || undefined,
        items: orderItems,
        paymentMethods: submittedEntries.map((e) => ({ paymentMethod: e.paymentMethod, amountPaid: e.amountPaid })),
        idempotencyKey,
        installments: paymentEntries.find((e) => e.paymentMethod === PaymentMethod.CreditCard)?.installments,
        ...(isGuest && {
          guestName,
          guestPhone: guestInfo.phone,
          guestStreet: guestInfo.street,
          guestNumber: guestInfo.number,
          guestNeighborhood: guestInfo.neighborhood,
          guestCity: guestInfo.city,
          guestState: guestInfo.state,
          guestZipCode: guestInfo.zipCode.replace(/\D/g, ''),
          guestComplement: guestInfo.complement || undefined,
          guestEmail: guestInfo.email || undefined,
          guestCpfCnpj: guestInfo.cpfCnpj || undefined,
          ...(guestInfo.babies.some((b) => b.name.trim())
            ? {
                guestBabies: guestInfo.babies
                  .filter((b) => b.name.trim())
                  .map((b) => ({
                    id: 0,
                    name: b.name.trim(),
                    birthdate: b.birthdate || undefined,
                    gender: b.gender ? Number(b.gender) : undefined,
                  })),
              }
            : {}),
        }),
      });

      const displayName = isGuest ? guestName : customerProfile?.name;
      const displayPhone = isGuest ? guestInfo.phone : customerProfile?.phone;
      const displayEmail = isGuest ? guestInfo.email : customerProfile?.email;

      trackPurchaseOnce(orderId, displayEmail, displayPhone);
      trackStoreEvent({
        type: StoreEventType.OrderPlaced,
        metadata: {
          orderId,
          total: totals.net,
          itemsCount: items.reduce((sum, i) => sum + i.quantity, 0),
        },
      });

      const whatsAppUrl = buildWhatsAppUrl({
        seller: selectedSeller,
        items,
        totals,
        orderId,
        paymentEntries: submittedEntries,
        customerName: displayName,
        customerPhone: displayPhone,
        observation: observation || undefined,
        installmentsInterestFree,
      });

      saveOrderConfirmation({
        orderId,
        whatsAppUrl,
        orderedAt: new Date().toISOString(),
        sellerName: selectedSeller.name,
        paymentMethods: Array.from(new Set(submittedEntries.map((e) => e.paymentMethod))),
        items: items.map((i) => ({
          name: i.productName,
          variant: [i.colorName, i.variantLabel].filter(Boolean).join(' · '),
          quantity: i.quantity,
        })),
        total: totals.net,
        ...(isGuest && { guest: { name: guestName, phone: guestInfo.phone, email: guestInfo.email } }),
      });
      completedRef.current = true;
      clearPersistedCheckoutState();
      setIsRedirecting(true);
      navigate(`/pedido/${orderId}/confirmado`, { replace: true });
      clearCart();
    } catch (err) {
      const stockError = extractInsufficientStock(err);
      if (stockError) {
        setStockConflict({ items: stockError.items, applied: false });
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      if (isUnauthorizedError(err)) {
        setSessionExpired(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      showToast(getOrderErrorMessage(err), 'error');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const confirmStepCustomerInfo: GuestInfo | undefined = !isAuthenticated
    ? guestInfo
    : customerProfile
    ? {
        ...DEFAULT_GUEST_INFO,
        name: customerProfile.name,
        phone: customerProfile.phone,
        street: customerProfile.street ?? '',
        number: customerProfile.number ?? '',
        neighborhood: customerProfile.neighborhood ?? '',
        city: customerProfile.city ?? '',
        state: customerProfile.state ?? '',
        zipCode: customerProfile.zipCode ?? '',
        complement: customerProfile.complement ?? '',
        email: customerProfile.email ?? '',
      }
    : undefined;

  const topBack =
    step === 'confirm'
      ? { label: 'Voltar', onClick: () => setStep('payment') }
      : step === 'payment' && !isAuthenticated
      ? { label: 'Voltar', onClick: () => setStep('guest-info') }
      : { label: 'Voltar para a sacola', onClick: () => navigate('/cart') };

  return (
    <div className={`mx-auto px-4 py-4 sm:py-8 ${CHECKOUT_MAX_WIDTH_CLASS}`}>
      <button
        type="button"
        onClick={topBack.onClick}
        className={`${TOUCH_TARGET_CLASS} inline-flex items-center gap-2 text-sm font-medium text-text-medium hover:text-chocolate-dark border border-champagne-dark hover:border-chocolate bg-white hover:bg-champagne px-4 py-2 rounded-full mb-3 sm:mb-6 transition-all duration-150`}
      >
        <ChevronLeftIcon className="w-4 h-4" />
        {topBack.label}
      </button>

      <div className="flex items-center gap-3 mb-3 sm:mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-text-dark">Finalizar pedido</h1>
      </div>

      {sessionExpired && !isAuthenticated && (
        <SessionExpiredNotice onContinueAsGuest={() => setSessionExpired(false)} />
      )}

      {stockConflict && (
        <StockConflictNotice
          items={stockConflict.items}
          applied={stockConflict.applied}
          onApply={applyStockConflict}
          onDismiss={() => setStockConflict(null)}
        />
      )}

      <StepIndicator step={step} isAuthenticated={isAuthenticated} />

      {step !== 'confirm' && <MobileOrderSummary items={items} totals={totals} />}

      {step === 'guest-info' && (
        <div className={CHECKOUT_SIDEBAR_GRID_CLASS}>
          <GuestInfoStep
            guestInfo={guestInfo}
            onChange={setGuestInfo}
            onNext={() => setStep('payment')}
            backLabel={topBack.label}
            onBack={topBack.onClick}
          />
          <aside className="hidden lg:block lg:sticky lg:top-20">
            <OrderSummaryCard items={items} totals={totals} />
          </aside>
        </div>
      )}

      {step === 'payment' && (
        <PaymentStep
          sellers={sellers}
          loadingSellers={loadingSellers}
          selectedSeller={selectedSeller}
          onSelectSeller={setSelectedSeller}
          items={items}
          totals={totals}
          paymentEntries={paymentEntries}
          paymentRemainder={paymentRemainder}
          paymentMode={paymentMode}
          hasPromotionalItems={hasPromotionalItems}
          paymentSettings={paymentSettings}
          onSelectCombineMethod={selectCombinePaymentMethod}
          onSwitchToDetailed={() => setPaymentMode('detailed')}
          onSwitchToCombine={switchToCombinePayment}
          onAddPaymentMethod={addPaymentMethod}
          onRemove={removePaymentMethod}
          onUpdate={updatePaymentEntry}
          onFillRemainder={fillRemainder}
          onNext={() => setStep('confirm')}
          isValid={isPaymentValid()}
          backLabel={topBack.label}
          onBack={topBack.onClick}
        />
      )}

      {step === 'confirm' && selectedSeller && (
        <ConfirmStep
          seller={selectedSeller}
          paymentEntries={paymentEntries}
          totals={totals}
          installmentsInterestFree={installmentsInterestFree}
          observation={observation}
          onObservationChange={setObservation}
          items={items}
          onSubmit={handleSubmit}
          submitting={submitting}
          customerInfo={confirmStepCustomerInfo}
          backLabel={topBack.label}
          onBack={topBack.onClick}
        />
      )}
    </div>
  );
}
